import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hook to fetch all custom NIP documents (kind 30817) for a specific pubkey
 */
export function useCustomNIPsList(pubkey: string | undefined, enabled = true) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['custom-nips-list', pubkey],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      try {
        const dittoRelay = nostr.relay('wss://relay.ditto.pub');

        const events = await dittoRelay.query([
          {
            kinds: [30817],
            authors: [pubkey],
            limit: 50,
          }
        ], { signal });

        return events.sort((a, b) => b.created_at - a.created_at);
      } catch (error) {
        console.error('Error fetching custom NIPs list:', error);
        return [];
      }
    },
    enabled: !!pubkey && enabled,
    staleTime: 0, // Always consider data stale to refetch on tab switch
  });
}

/**
 * Hook to fetch a single custom NIP by d-tag
 */
export function useCustomNIP(pubkey: string | undefined, dTag: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['custom-nip', pubkey, dTag],
    queryFn: async (c) => {
      if (!pubkey || !dTag) return null;

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      try {
        const dittoRelay = nostr.relay('wss://relay.ditto.pub');

        const events = await dittoRelay.query([
          {
            kinds: [30817],
            authors: [pubkey],
            '#d': [dTag],
            limit: 1,
          }
        ], { signal });

        return events[0] || null;
      } catch (error) {
        console.error('Error fetching custom NIP:', error);
        return null;
      }
    },
    enabled: !!pubkey && !!dTag,
  });
}

/**
 * Extract NIP title from event
 */
export function extractNIPTitle(event: NostrEvent): string | null {
  const titleTag = event.tags.find(([name]) => name === 'title');
  return titleTag?.[1] || null;
}

/**
 * Extract NIP d-tag identifier
 */
export function extractNIPDTag(event: NostrEvent): string | null {
  const dTag = event.tags.find(([name]) => name === 'd');
  return dTag?.[1] || null;
}
